/**
 * GuestbookEntryCard — 방명록 개별 카드 (방문자/소유자 공용)
 */

import type { GuestbookEntryPublic, GuestbookEntryOwner } from '@cugumi/shared/schemas'
import { Badge } from '@/components/ui/index.js'
import styles from '../GuestbookBlock.module.css'

interface GuestbookEntryCardProps {
  entry: GuestbookEntryPublic | GuestbookEntryOwner
  isOwnerView?: boolean
  /** 방문자 뷰에서 삭제 버튼 표시 여부 */
  showDeleteBtn?: boolean
  onHideToggle?: (entryId: string, isHidden: boolean) => void
  onDelete?: (entryId: string) => void
}

/** 상대 시간 포맷 (방금 전, N분 전, N시간 전, N일 전, 날짜) */
function formatRelativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = now - then

  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return '방금 전'
  if (minutes < 60) return `${minutes}분 전`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}시간 전`

  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}일 전`

  const d = new Date(dateStr)
  return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()}`
}

export function GuestbookEntryCard({
  entry,
  isOwnerView = false,
  showDeleteBtn = false,
  onHideToggle,
  onDelete,
}: GuestbookEntryCardProps) {
  const ownerEntry = entry as GuestbookEntryOwner
  const isHidden = isOwnerView && ownerEntry.isHidden
  const isPrivate = isOwnerView && ownerEntry.isPrivate

  return (
    <div className={`${styles.card} ${isHidden ? styles.cardHidden : ''}`}>
      <div className={styles.cardHeader}>
        <span className={styles.cardName}>{entry.name}</span>
        <div className={styles.cardHeaderRight}>
          <span className={styles.cardTime}>{formatRelativeTime(entry.createdAt)}</span>
          {!isOwnerView && showDeleteBtn && (
            <button
              type="button"
              className={`${styles.cardActionBtn} ${styles.cardActionBtnDanger}`}
              onClick={() => onDelete?.(entry.id)}
            >
              삭제
            </button>
          )}
        </div>
      </div>
      <p className={styles.cardMessage}>{entry.message}</p>
      {isOwnerView && (
        <div className={styles.cardMeta}>
          {isPrivate && <Badge color="blue">비공개</Badge>}
          {isHidden && <Badge color="yellow">숨김</Badge>}
          <div className={styles.cardActions}>
            <button
              type="button"
              className={styles.cardActionBtn}
              onClick={() => onHideToggle?.(entry.id, !ownerEntry.isHidden)}
            >
              {ownerEntry.isHidden ? '표시' : '숨김'}
            </button>
            <button
              type="button"
              className={`${styles.cardActionBtn} ${styles.cardActionBtnDanger}`}
              onClick={() => onDelete?.(entry.id)}
            >
              삭제
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
